import classNames from 'classnames';
import * as React from 'react';
import type { OverlayContainerProps } from '../Overlay/WithOverlay';
import { WithOverlay } from '../Overlay/WithOverlay';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { Button } from '~/Partials/Button/Button';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import type { LinkViewModel } from '~/Partials/Link/LinkViewModel.csharp';
import { List } from '~/Partials/List/List';
import { Shortcut } from '~/Partials/Shortcut/Shortcut';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import type { Theme } from '~/Utils/Theme';
import styles from './StudyPageLinkList.module.css';
import type { StudyPageLinkListVariant } from './StudyPageLinkListVariant';

export interface StudyPageLinkListProps {
    overlay?: OverlayContainerProps<HtmlString>;
    variant?: StudyPageLinkListVariant;
    items?: LinkViewModel[];
    teaser?: HtmlString;
    theme?: Pick<Theme, 'withRoundedCorners'>;
}

const StudyPageLinkListOverlayContent: React.FC<
    Pick<OverlayContainerProps<HtmlString>, 'overlayLabel' | 'content'>
> = props => {
    const { overlayLabel, content } = props;

    return (
        <>
            <h3 className={styles.OverlayContentHeading}>{overlayLabel}</h3>
            <HtmlDiv className="RichtextContent RichtextContent--dark" html={content} />
        </>
    );
};

export const StudyPageLinkList: React.FC<StudyPageLinkListProps> = props => {
    const contextualTheme = useTheme();
    const { teaser, items, overlay, variant = 'default', theme = contextualTheme } = props;
    const { withRoundedCorners } = theme;

    const richtextVariant = variant === 'dark' ? 'light' : 'dark';

    return (
        <div
            className={classNames(
                styles.StudyPageLinkList,
                styles[`Variant--${variant}`],
                withRoundedCorners && styles['Variant--rounded']
            )}
        >
            {teaser && (
                <HtmlDiv
                    html={teaser}
                    className={classNames(styles.Teaser, 'RichtextContent', `RichtextContent--${richtextVariant}`)}
                />
            )}
            <div>
                {items && items.length > 0 && (
                    <List
                        items={items}
                        className={styles.Items}
                        renderItem={(item, id) => (
                            <li key={`${item.href}-${id}`}>
                                <Shortcut {...item} variant={variant === 'default' ? 'dark' : 'light'} />
                            </li>
                        )}
                    />
                )}
                {overlay && (
                    <WithOverlay
                        overlayLabel={overlay.overlayLabel}
                        overlayContent={
                            <StudyPageLinkListOverlayContent
                                content={overlay.content}
                                overlayLabel={overlay.overlayLabel}
                            />
                        }
                        closeLabel={overlay.closeLabel}
                    >
                        {triggerButtonProps => (
                            <Button
                                className={styles.Button}
                                type={ButtonType.Clear}
                                title={overlay.openLabel}
                                {...triggerButtonProps}
                            >
                                <span className="visuallyHidden">{overlay.openLabel}</span>
                                <SvgIcon className={styles.Icon} iconName="plus-circle" />
                            </Button>
                        )}
                    </WithOverlay>
                )}
            </div>
        </div>
    );
};
